import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Search, UserPlus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ModuleTitleLIFT } from "@/components/lift/ModuleTitleLIFT";

/**
 * Employee page to invite peers for the given cycle. Managers can also
 * invite peers for a report by hitting this page with ?forEmployeeId=xxx
 * (used from the ManagerEvaluation view).
 */
export default function PeerRequestsPage() {
  const { cycleId } = useParams();
  const { user } = useAuth();
  const params = new URLSearchParams(window.location.search);
  const forEmployeeId = params.get("forEmployeeId");
  const subjectId = forEmployeeId || user.id;
  const isManagerRequest = !!forEmployeeId;

  const [requests, setRequests] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);
  const [dueDate, setDueDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const [r, d] = await Promise.all([
        api.get(`/lift/peer-requests/${cycleId}/${subjectId}`),
        api.get("/users/directory"),
      ]);
      setRequests(r.data);
      setDirectory(d.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleId, subjectId]);

  const alreadyRequested = useMemo(() => {
    // We only receive peer_name in list when authorized; ids not exposed to protect anonymity for others.
    // Use names present in the list to grey them out in the picker.
    return new Set((requests || []).map((r) => r.peer_name).filter(Boolean));
  }, [requests]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return directory
      .filter((u) => u.id !== subjectId)
      // employee cannot pick their own manager
      .filter((u) => (isManagerRequest ? true : u.id !== user.manager_id))
      .filter((u) =>
        query
          ? [u.name, u.job_title, u.department].some((v) => v && v.toLowerCase().includes(query))
          : true
      )
      .slice(0, 60);
  }, [directory, q, subjectId, user, isManagerRequest]);

  const toggle = (id) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const submit = async () => {
    if (selected.length === 0) {
      toast.error("Choose at least one peer.");
      return;
    }
    setSubmitting(true);
    try {
      const url = isManagerRequest
        ? `/lift/peer-requests/${cycleId}/for/${subjectId}`
        : `/lift/peer-requests/${cycleId}`;
      await api.post(url, { peer_ids: selected, due_date: dueDate || null });
      toast.success(`Requested feedback from ${selected.length} peer${selected.length > 1 ? "s" : ""}.`);
      setSelected([]);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="lift-peer-requests-page">
      <Link to={isManagerRequest ? `/lift/manager/${cycleId}` : `/lift/cycle/${cycleId}`} className="patria-btn-ghost -ml-3">
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> Back
      </Link>
      <div>
        <ModuleTitleLIFT />
        <h1 className="mt-3 text-4xl font-serif text-forest-900">
          {isManagerRequest ? "Request peer feedback for your report" : "Ask peers for feedback"}
        </h1>
        <p className="mt-2 max-w-2xl text-charcoal-soft">
          Your peers will be asked for a short, kind reflection.
          {" "}Responses are shared with the employee anonymously, and only when at least 3 have been received.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="patria-card p-6">
          <h3 className="text-xl font-serif text-forest-900 mb-3">
            Pick Teammates
          </h3>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-charcoal-muted" strokeWidth={1.5} />
            <input
              className="patria-input pl-9 w-full"
              placeholder="Search teammates..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="peer-picker-search"
            />
          </div>
          <ul className="max-h-96 overflow-y-auto divide-y divide-sage-300/60">
            {filtered.map((u) => {
              const already = alreadyRequested.has(u.name);
              const on = selected.includes(u.id);
              return (
                <li key={u.id} className="py-2 flex items-center gap-3">
                  <input
                    type="checkbox"
                    className="rounded"
                    checked={on}
                    onChange={() => toggle(u.id)}
                    disabled={already}
                    data-testid={`peer-pick-${u.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-forest-900 truncate">{u.name}</p>
                    <p className="text-xs text-charcoal-muted truncate">
                      {u.job_title || "—"} · {u.department || "—"}
                    </p>
                  </div>
                  {already && <span className="patria-chip-green">Requested</span>}
                </li>
              );
            })}
            {filtered.length === 0 && (
              <li className="py-6 italic text-charcoal-muted">No teammates match.</li>
            )}
          </ul>

          <div className="mt-4 flex flex-col sm:flex-row items-end gap-3">
            <div className="flex-1 w-full">
              <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1">
                Response due date (optional)
              </label>
              <input
                type="date"
                className="patria-input w-full"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                data-testid="peer-due-date"
              />
            </div>
            <button
              className="patria-btn-primary w-full sm:w-auto"
              onClick={submit}
              disabled={submitting || selected.length === 0}
              data-testid="peer-submit"
            >
              <UserPlus className="h-4 w-4" strokeWidth={1.5} />
              Send {selected.length ? `(${selected.length})` : ""}
            </button>
          </div>
        </div>

        <div className="patria-card p-6" data-testid="peer-existing-requests">
          <h3 className="text-xl font-serif text-forest-900 mb-3">
            Existing Requests · {requests.length}
          </h3>
          {requests.length === 0 && (
            <p className="italic text-charcoal-muted">No requests yet.</p>
          )}
          <ul className="divide-y divide-sage-300/60">
            {requests.map((r) => (
              <li key={r.id} className="py-3 flex items-center gap-3">
                <span
                  className={
                    r.status === "submitted"
                      ? "patria-chip-green"
                      : r.status === "declined"
                      ? "patria-chip-terra"
                      : "patria-chip-neutral"
                  }
                >
                  {r.status}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-forest-900 truncate">
                    {r.peer_name || "(anonymous — not shown)"}
                  </p>
                  <p className="text-xs text-charcoal-muted">
                    {r.requester_type === "manager" ? "Manager-requested" : "You requested"}
                    {r.due_date ? ` · due ${r.due_date}` : ""}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
